import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { ChevronLeft, Trash2, ExternalLink, Bookmark as BookmarkIcon, Search } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { getBookmarks, deleteBookmark } from '../services/api';

export const Bookmarks: React.FC = () => {
  const { user } = useAuth();
  const { surfaceClass, bgClass } = useTheme();
  const [selectedBookId, setSelectedBookId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getBookmarks();
        setBookmarks(res.bookmarks || []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
  }, [user]);

  if (!user) return <Navigate to="/" />;

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this bookmark?')) return;
    try {
      await deleteBookmark(id);
      setBookmarks(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const booksWithBookmarks = bookmarks
    .map(b => ({ id: b.book_id, title: b.title, author: b.author }))
    .filter((v, i, arr) => arr.findIndex(x => x.id === v.id) === i);

  const filteredBookmarks = bookmarks.filter(b => {
    const matchesBook = selectedBookId === 'all' || b.book_id === selectedBookId;
    const matchesSearch =
      b.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesBook && matchesSearch;
  });

  const groupedBookmarks = filteredBookmarks.reduce((acc, bookmark) => {
    if (!acc[bookmark.book_id]) {
      acc[bookmark.book_id] = [];
    }
    acc[bookmark.book_id].push(bookmark);
    return acc;
  }, {} as Record<string, typeof bookmarks>);

  return (
    <div className="pb-24 px-6 pt-6 animate-in slide-in-from-right duration-500 min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/profile" className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
          <ChevronLeft size={24} />
        </Link>
        <div>
          <p className="text-xs uppercase tracking-wider opacity-50">My Evidence Log</p>
          <h1 className="font-serif text-2xl font-bold">Bookmarks</h1>
        </div>
      </div>

      {loading && <p className="text-sm opacity-60">Loading...</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Search & Filter Bar */}
      <div className="mb-8 space-y-4">
        <div className={`relative rounded-xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden ${surfaceClass}`}>
           <div className="absolute left-3 top-3 opacity-40"><Search size={18} /></div>
           <input 
             type="text" 
             placeholder="Filter through your saved lines by title, keyword, or case."
             className="w-full bg-transparent p-3 pl-10 outline-none text-sm placeholder:opacity-50"
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
           />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
           <button 
             onClick={() => setSelectedBookId('all')}
             className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${selectedBookId === 'all' ? 'bg-brand-blue text-white shadow-md' : 'bg-black/5 dark:bg-white/10 opacity-70 hover:opacity-100'}`}
           >
             All Books
           </button>
           {booksWithBookmarks.map(book => (
             <button 
               key={book.id}
               onClick={() => setSelectedBookId(book.id)}
               className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${selectedBookId === book.id ? 'bg-brand-blue text-white shadow-md' : 'bg-black/5 dark:bg-white/10 opacity-70 hover:opacity-100'}`}
             >
               {book.title}
             </button>
           ))}
        </div>
      </div>

      {/* Bookmarks List */}
      <div className="space-y-8">
        {Object.keys(groupedBookmarks).length > 0 ? (
          Object.keys(groupedBookmarks).map(bookId => {
            const bookInfo = booksWithBookmarks.find(b => b.id === bookId);
            const bookBookmarks = groupedBookmarks[bookId];

            return (
              <div key={bookId} className="animate-in fade-in duration-500">
                {selectedBookId === 'all' && (
                   <div className="flex items-center gap-3 mb-4 opacity-80">
                      <GeneratedCover title={bookInfo?.title || 'Untitled'} author={bookInfo?.author || ''} size="sm" className="w-8 h-12 text-[5px]" />
                      <h2 className="font-bold text-lg leading-tight">{bookInfo?.title || bookId}</h2>
                      <span className="text-xs opacity-50 bg-black/5 dark:bg-white/10 px-2 py-0.5 rounded-full">{bookBookmarks.length}</span>
                   </div>
                )}

                <div className="space-y-3 pl-0 md:pl-4">
                  {bookBookmarks.map(bookmark => (
                    <div key={bookmark.id} className={`group relative p-5 rounded-xl border border-black/5 dark:border-white/5 shadow-sm hover:shadow-md transition-all ${surfaceClass}`}>
                       <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider opacity-50 text-brand-blue">
                             <BookmarkIcon size={12} fill="currentColor" />
                             <span>{bookmark.chapter_id?.replace('ch-', 'Chapter ')}</span>
                          </div>
                          <span className="text-[10px] opacity-40">{bookmark.created_at}</span>
                       </div>

                       <p className="font-serif italic text-base leading-relaxed opacity-80 mb-4 border-l-2 border-brand-blue/30 pl-3">
                         "{bookmark.excerpt || 'No excerpt'}"
                       </p>

                       <div className="flex justify-between items-center pt-2 border-t border-black/5 dark:border-white/5">
                          <button 
                            onClick={() => handleDelete(bookmark.id)}
                            className="text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1.5 rounded transition-colors flex items-center gap-1.5 opacity-60 hover:opacity-100"
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                          
                          <Link to={`/book/${bookmark.book_id}/read/${bookmark.chapter_id}`}>
                            <Button size="sm" variant="ghost" className="text-brand-blue hover:text-brand-hover gap-1 pr-0">
                               Go to location <ExternalLink size={14} />
                            </Button>
                          </Link>
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-20 opacity-80">
             <div className="w-16 h-16 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookmarkIcon size={24} />
             </div>
             <p className="text-lg font-medium mb-1">No Evidence Recorded</p>
             <p className="text-sm opacity-70 mb-2">You haven’t bookmarked anything yet.</p>
             <p className="text-sm opacity-60 leading-relaxed">
               While reading, mark the lines you want to return to — details, phrases, or moments that feel important, even if you’re not sure why.
             </p>
          </div>
        )}
      </div>
    </div>
  );
};
